import { z } from 'zod'

import { buildAppointmentWindow } from './appointment-window'

/** One clinic day's complete NexHealth read, bounded like onboarding-api. */
export const MAX_APPOINTMENTS_PER_CLINIC_DAY = 200

const HOUR_MS = 3_600_000
const DAY_MS = 24 * HOUR_MS
// Server cadence plus scheduling slack: the current day is reconciled every
// 4 h and the next seven days are re-read daily (onboarding-api maintenance).
export const CURRENT_DAY_EXPECTED_AGE_MS = 6 * HOUR_MS
export const UPCOMING_DAY_EXPECTED_AGE_MS = 30 * HOUR_MS
export const UPCOMING_REFRESH_DAYS = 7
// Clinic days Flio keeps synced: seven past days, today and 45 ahead.
export const PAST_SYNC_DAYS = 7
export const FUTURE_SYNC_DAYS = 45

const windowCoverageSchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.literal('COMPLETE'),
    windowStartAtMs: z.number().int().positive(),
    windowEndBeforeMs: z.number().int().positive(),
    clinicTimeZone: z.string().min(1),
    appointmentCount: z
      .number()
      .int()
      .min(0)
      .max(MAX_APPOINTMENTS_PER_CLINIC_DAY),
    observedAtMs: z.number().int().positive(),
  })
  .strict()

export type WindowCoverage = z.infer<typeof windowCoverageSchema>

/**
 * Every covered clinic day by window start, from the clinic's published day
 * coverage document ({schemaVersion, tenantUid, windows}). Null when nothing
 * is published for this clinic (no claim is made about any day then); an
 * empty map when it is published but no day has synced yet. A malformed day
 * is simply not covered.
 */
export function parseWindowCoverage(
  value: unknown,
  tenantId: string
): Map<number, WindowCoverage> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  const document = value as Record<string, unknown>
  if (document.schemaVersion !== 1 || document.tenantUid !== tenantId) {
    return null
  }
  const windows = document.windows
  const result = new Map<number, WindowCoverage>()
  if (
    windows === null ||
    typeof windows !== 'object' ||
    Array.isArray(windows)
  ) {
    return result
  }
  for (const [key, raw] of Object.entries(windows as Record<string, unknown>)) {
    const parsed = windowCoverageSchema.safeParse(raw)
    if (parsed.success && String(parsed.data.windowStartAtMs) === key) {
      result.set(parsed.data.windowStartAtMs, parsed.data)
    }
  }
  return result
}

export type DaySyncState =
  /** Read recently enough for its place in the schedule. */
  | 'current'
  /** Expected to be fresh (today or the next seven days) but is not. */
  | 'stale'
  /** Inside the synced range but never read completely. */
  | 'missing'
  /** Read, with no freshness expectation (past days, days far ahead). */
  | 'recorded'
  /** Outside the synced range and not read. */
  | 'outside'

export type DaySync = {
  date: string
  state: DaySyncState
  observedAtMs: number | null
}

function dateOffset(date: string, today: string) {
  return Math.round(
    (Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) /
      DAY_MS
  )
}

export function clinicDate(timestamp: number, timeZone: string) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(timestamp)
}

export function daySync(
  coverage: Map<number, WindowCoverage>,
  date: string,
  timeZone: string,
  nowMs: number
): DaySync | null {
  const window = buildAppointmentWindow([date], timeZone)
  if (!window) return null
  const offset = dateOffset(date, clinicDate(nowMs, timeZone))
  const header = coverage.get(window.startAtMs)
  const covered =
    header &&
    header.windowEndBeforeMs === window.endBeforeMs &&
    header.clinicTimeZone === timeZone
      ? header
      : null
  if (!covered) {
    const inRange = offset >= -PAST_SYNC_DAYS && offset <= FUTURE_SYNC_DAYS
    return { date, state: inRange ? 'missing' : 'outside', observedAtMs: null }
  }
  const expected =
    offset === 0
      ? CURRENT_DAY_EXPECTED_AGE_MS
      : offset > 0 && offset <= UPCOMING_REFRESH_DAYS
        ? UPCOMING_DAY_EXPECTED_AGE_MS
        : null
  const state: DaySyncState =
    expected === null
      ? 'recorded'
      : nowMs - covered.observedAtMs > expected
        ? 'stale'
        : 'current'
  return { date, state, observedAtMs: covered.observedAtMs }
}

export function relativeSyncTime(timestamp: number, nowMs: number) {
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  const minutes = Math.round((timestamp - nowMs) / 60_000)
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour')
  return formatter.format(Math.round(hours / 24), 'day')
}

function dayLabel(date: string) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(Date.parse(`${date}T00:00:00Z`))
}

function listOfDays(days: DaySync[]) {
  const labels = days.slice(0, 3).map((day) => dayLabel(day.date))
  return days.length > 3
    ? `${labels.join(', ')} and ${days.length - 3} more`
    : labels.join(', ')
}

export type SyncNotice = { tone: 'neutral' | 'warning'; message: string }

/** One line about how current the shown days are; null when nothing is known. */
export function syncNotice(days: DaySync[], nowMs: number): SyncNotice | null {
  const missing = days.filter((day) => day.state === 'missing')
  if (missing.length) {
    return {
      tone: 'warning',
      message: `Not synced from NexHealth yet: ${listOfDays(missing)}. Appointments on ${missing.length === 1 ? 'this day' : 'these days'} may be missing.`,
    }
  }
  const stale = days.filter((day) => day.state === 'stale')
  if (stale.length) {
    const oldest = Math.min(...stale.map((day) => day.observedAtMs ?? nowMs))
    return {
      tone: 'warning',
      message: `Last synced from NexHealth ${relativeSyncTime(oldest, nowMs)}: ${listOfDays(stale)}. Recent changes in NexHealth may not appear yet.`,
    }
  }
  const current = days.filter((day) => day.state === 'current')
  const synced = current.length
    ? current
    : days.filter((day) => day.state === 'recorded')
  if (!synced.length) return null
  const oldest = Math.min(...synced.map((day) => day.observedAtMs ?? nowMs))
  return {
    tone: 'neutral',
    message: `${current.length ? 'Synced with' : 'Last synced from'} NexHealth ${relativeSyncTime(oldest, nowMs)}.`,
  }
}
